import * as THREE from 'three'

/**
 * User-editable rotation offsets for Virtual Tour skyboxes.
 *
 * Six preset operations the user can chain in a UI toolbar; the result is
 * persisted to scene_properties.rotationOffset (scene-level) or
 * feature_properties.rotationOffset (viewpoint-level, overrides scene).
 *
 * Storage shape: `{"matrix":[16 floats]}` column-major to match THREE.Matrix4
 * native ordering, so `Matrix4.fromArray()` reads it cleanly.
 *
 * The render path in virtual-tour.service.ts `_applySkyboxMatrixTransformations`
 * reads the effective offset and right-multiplies its quaternion onto the
 * skybox.quaternion — matching the local-frame post-multiply paradigm of the
 * surrounding `skybox.rotateOnAxis(...)` chain.
 */

export type RotationOp = 'swapYZ' | 'flipVertical' | 'flipHorizontal' | 'rotate90Left' | 'rotate90Right' | 'reset'

const HALF_PI = Math.PI / 2

const buildPreset = (axis: THREE.Vector3, angle: number): THREE.Matrix4 =>
    new THREE.Matrix4().makeRotationAxis(axis.normalize(), angle)

/** Each preset is the rotation applied when the user clicks the button — premultiplied onto the current draft. */
export const ROTATION_PRESETS: Readonly<Record<Exclude<RotationOp, 'reset'>, THREE.Matrix4>> = Object.freeze({
    swapYZ:         buildPreset(new THREE.Vector3(1, 0, 0), HALF_PI),   // R_x(+90°): swaps Y↔Z with sign-fix to det=+1
    flipVertical:   buildPreset(new THREE.Vector3(1, 0, 0), Math.PI),   // R_x(180°)
    flipHorizontal: buildPreset(new THREE.Vector3(0, 0, 1), Math.PI),   // R_z(180°)
    rotate90Left:   buildPreset(new THREE.Vector3(0, 0, 1), HALF_PI),   // R_z(+90°)
    rotate90Right:  buildPreset(new THREE.Vector3(0, 0, 1), -HALF_PI),  // R_z(-90°)
})

/** Apply a preset to the current draft matrix. 'reset' clears back to identity. */
export function applyPresetOp(current: THREE.Matrix4, op: RotationOp): THREE.Matrix4 {
    if (op === 'reset') return new THREE.Matrix4()
    const preset = ROTATION_PRESETS[op]
    return new THREE.Matrix4().multiplyMatrices(preset, current)
}

/** Parse a stored rotationOffset value (string OR parsed object — both shapes appear). Returns identity on missing / malformed. */
export function parseRotationOffset(value: unknown): THREE.Matrix4 {
    if (value == null || value === '') return new THREE.Matrix4()
    let payload: unknown = value
    if (typeof payload === 'string') {
        try { payload = JSON.parse(payload) }
        catch { return new THREE.Matrix4() }
    }
    const arr = (payload as { matrix?: number[] })?.matrix
    if (!Array.isArray(arr) || arr.length !== 16 || !arr.every(n => Number.isFinite(n))) {
        return new THREE.Matrix4()
    }
    return new THREE.Matrix4().fromArray(arr)
}

/** Serialize a Matrix4 to the on-disk JSON string format. */
export function serializeRotationOffset(matrix: THREE.Matrix4): string {
    return JSON.stringify({ matrix: matrix.toArray() })
}

/** True if this matrix is the identity (within a tiny floating-point tolerance). */
export function isIdentityRotation(matrix: THREE.Matrix4): boolean {
    const I = new THREE.Matrix4().elements
    const m = matrix.elements
    for (let i = 0; i < 16; i++) {
        if (Math.abs(m[i] - I[i]) > 1e-9) return false
    }
    return true
}
